if (!customElements.get('pdp-tabs')) {
  customElements.define(
    'pdp-tabs',
    class PdpTabs extends HTMLElement {
      constructor() {
        super();
        this.onTabClick = this.onTabClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('.pdp-tab[role="tab"]'));
        this.panels = Array.from(
          this.querySelectorAll('.pdp-tabs__item[role="tabpanel"]'),
        );

        if (this.tabs.length === 0 || this.panels.length === 0) return;

        // Initial active index = the panel that isn't hidden, default to first tab.
        const visiblePanel = this.panels.find(
          (panel) => panel.getAttribute('aria-hidden') === 'false',
        );
        const initialIndex = visiblePanel
          ? visiblePanel.dataset.index
          : this.tabs[0].dataset.index;

        this.tabs.forEach((tab) => {
          const button = tab.querySelector('button');
          if (button) button.type = 'button';
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onKeydown);
        });

        this.activate(initialIndex);
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
          tab.removeEventListener('keydown', this.onKeydown);
        });
      }

      onTabClick(event) {
        const tab = event.currentTarget;
        this.activate(tab.dataset.index);
      }

      onKeydown(event) {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        if (currentIndex === -1) return;

        let nextIndex = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabs.length - 1;
        }

        if (nextIndex === null) return;

        event.preventDefault();
        const nextTab = this.tabs[nextIndex];
        this.activate(nextTab.dataset.index);
        const button = nextTab.querySelector('button');
        (button || nextTab).focus();
      }

      activate(index) {
        const activeIndex = String(index);

        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.index === activeIndex;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          const button = tab.querySelector('button');
          if (button) button.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.index === activeIndex;
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          // Hidden panels are display:none and report zero height, so only
          // measure/set up a panel once it's actually visible.
          if (isActive) this.setupPanel(panel);
        });
      }

      setupPanel(panel) {
        if (panel.dataset.pdpTabsReady) return;
        panel.dataset.pdpTabsReady = 'true';

        // The clip wrapper + read-more button are rendered in the markup;
        // JS only decides whether this panel actually overflows the clip.
        const content = panel.querySelector('.pdp-tabs__item-content');
        const button = panel.querySelector('.pdp-tabs__read-more');
        if (!content) return;

        // Short enough to show in full — drop the clip + the read-more button.
        if (content.scrollHeight <= content.clientHeight + 4) {
          content.classList.remove('pdp-tabs__item-content');
          if (button) button.remove();
          return;
        }

        if (!button) return;
        // Expanding one expands all — toggle the flag on the wrapping element
        // and let CSS reveal/clip every panel + fade. Read-more doubles as
        // Read-less so it can collapse (hide) the content once extended.
        button.addEventListener('click', () => {
          const expanded = this.classList.toggle('is-expanded');
          button.textContent = expanded ? 'Read less' : 'Read more';
        });
      }
    },
  );
}
